// modules/fin/src/manifest.ts — FIN's nav contribution, one export per portal
// surface (the modules/lib precedent). Manifests import from ./manifest, NEVER
// ./fe. The API re-checks every capability on every route (ADR-001 §14.2) —
// the menu only HIDES affordances.
import {
  Banknote,
  BookOpenCheck,
  CircleDollarSign,
  LayoutDashboard,
  Receipt,
  HandCoins,
  ReceiptText,
  ShieldCheck,
  Wallet,
} from "lucide-solid";
import type { ModuleManifest } from "@erp/auth-kit/nav";

/**
 * apps/portals/admin — the /finance console. Console admission is any of
 * FIN_CONSOLE_PERMS (schemas/grants.ts); each destination re-gates on its own
 * capability so a future narrow grant surfaces only its screens.
 */
export const finAdminNav: ModuleManifest = {
  id: "fin",
  module: "fin",
  label: "Finance",
  to: "/finance",
  icon: CircleDollarSign,
  // ONE section of collapsible GROUPS (restructured 2026-09-02, the lib
  // precedent): parents with `children` turn on the sidebar's hierarchy rail.
  // Both parents' `to` is the module landing (matching-only; toggles, not
  // links) — the child-first tie-break lights Overview on /finance itself.
  // Parents carry no perm — visibility comes from the surviving children.
  sections: [
    {
      id: "fin-nav",
      label: "Finance",
      items: [
        {
          id: "fin-billing",
          label: "Billing",
          to: "/finance",
          icon: ReceiptText,
          children: [
            { id: "fin-overview", label: "Overview", to: "/finance", icon: LayoutDashboard, perm: "fin:read" },
            { id: "fin-invoices", label: "Invoices", to: "/finance/invoices", icon: ReceiptText, perm: "fin:read" },
            { id: "fin-verification", label: "Verification", to: "/finance/verification", icon: ShieldCheck, perm: "fin:verify" },
            // ADR-008 §1. The FIRST nav item anywhere to carry fin:cash — the
            // capability existed as a grant and gated nothing, so the one role
            // fenced to this duty had no way to reach it.
            { id: "fin-desk", label: "Cash desk", to: "/finance/desk", icon: HandCoins, perm: "fin:cash" },
            // HIDDEN leaves (audit UX-18): no menu row, but a name for the
            // breadcrumb to land on and the sidebar to open Billing for. Without
            // it "/finance/record" resolved to Overview — the wrong row lit and
            // the trail read "Finance › Billing › Overview".
            { id: "fin-record", label: "Record offline payment", to: "/finance/record", perm: "fin:manage", hidden: true },
          ],
        },
        {
          id: "fin-configuration",
          label: "Configuration",
          to: "/finance",
          icon: Banknote,
          children: [
            { id: "fin-accounts", label: "Accounts", to: "/finance/accounts", icon: Banknote, perm: "fin:manage" },
            { id: "fin-ledger", label: "Ledger", to: "/finance/ledger", icon: BookOpenCheck, perm: "fin:read" },
          ],
        },
      ],
    },
  ],
};

/**
 * apps/portals/student — the payer surface under the account area: my
 * invoices, "how to pay", submit a claim, payment history. Gated fin:pay
 * (student/patron/candidate/parent hold it via FIN_GRANTS).
 */
export const finStudentNav: ModuleManifest = {
  id: "fin-account",
  module: "fin",
  label: "Payments",
  to: "/account/payments",
  icon: Wallet,
  sections: [
    {
      id: "fin-my-payments",
      label: "My account",
      items: [
        { id: "fin-my-payments-home", label: "Payments", to: "/account/payments", icon: Wallet, perm: "fin:pay" },
      ],
    },
  ],
};

/**
 * apps/portals/staff — the payments desk (the lib-scoped verification queue).
 * DELIBERATELY carries NO fin permission: desk staff hold lib:desk, not fin:*,
 * and the SCREEN gates through the module-scoped verifier rule (fin:verify OR
 * <module>:desk OR <module>:admin — enforced server-side by allowedToVerify).
 * The nav item rides lib:desk so the affordance shows exactly for the staff the
 * rule admits on this portal.
 */
export const finStaffNav: ModuleManifest = {
  id: "fin-desk",
  module: "fin",
  label: "Payments",
  to: "/payments",
  icon: Receipt,
  sections: [
    {
      id: "fin-desk-queue",
      label: "Payments desk",
      items: [{ id: "fin-desk-payments", label: "Payments", to: "/payments", icon: Receipt, perm: "lib:desk" }],
    },
  ],
};
